import type { CustomRouteInput, StageMode } from '@edms/shared';
import { useState } from 'react';
import { UserSelect, useUsersList } from '../lib/tasks-ui';
import { Person } from '../lib/ui';

/** Значение в списке маршрутов: «Свой маршрут» вместо маршрута из справочника. */
export const CUSTOM_ROUTE = '__custom__';

type Stage = CustomRouteInput['stages'][number];

export const newCustomStage = (n: number): Stage => ({ name: `Этап ${n}`, mode: 'parallel', userIds: [], deadlineHours: null, instruction: null });
export const emptyCustomRoute = (): CustomRouteInput => ({ stages: [newCustomStage(1)] });

/** Что не так со своим маршрутом — до отправки на сервер; null — всё заполнено. */
export function customRouteProblem(route: CustomRouteInput): string | null {
  if (route.stages.length === 0) return 'Добавьте хотя бы один этап';
  const empty = route.stages.findIndex((s) => s.userIds.length === 0);
  if (empty >= 0) return `В этапе ${empty + 1} нет участников`;
  const unnamed = route.stages.findIndex((s) => !s.name.trim());
  if (unnamed >= 0) return `Назовите этап ${unnamed + 1}`;
  return null;
}

const move = <T,>(list: T[], i: number, d: -1 | 1): T[] => {
  const next = [...list];
  [next[i], next[i + d]] = [next[i + d]!, next[i]!];
  return next;
};

/**
 * Свой маршрут для одного документа: этапы «одновременно» или «по очереди»,
 * в каждом — конкретные сотрудники. Автор в свой маршрут не добавляется.
 */
export function CustomRouteEditor({ value, onChange, authorId, error }: { value: CustomRouteInput; onChange: (v: CustomRouteInput) => void; authorId: string; error?: string }) {
  const users = useUsersList();
  const [adding, setAdding] = useState<Record<number, string>>({});
  const stages = value.stages;
  const setStages = (next: Stage[]) => onChange({ stages: next });
  const setStage = (i: number, patch: Partial<Stage>) => setStages(stages.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const person = (id: string) => users.data?.find((u) => u.id === id);
  const problem = customRouteProblem(value);

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {stages.map((stage, i) => (
        <fieldset key={i} className="stage-edit">
          <legend>Этап {i + 1}</legend>
          <div className="stage-edit-head">
            <input className="input" value={stage.name} maxLength={200} onChange={(e) => setStage(i, { name: e.target.value })} aria-label="Название этапа" />
            <select className="select" value={stage.mode} onChange={(e) => setStage(i, { mode: e.target.value as StageMode })} aria-label="Порядок">
              <option value="parallel">Все одновременно</option>
              <option value="sequential">По очереди</option>
            </select>
            <span className="stage-tools">
              <button type="button" className="btn btn-ghost btn-sm" disabled={i === 0} onClick={() => setStages(move(stages, i, -1))} aria-label="Этап выше">
                ↑
              </button>
              <button type="button" className="btn btn-ghost btn-sm" disabled={i === stages.length - 1} onClick={() => setStages(move(stages, i, 1))} aria-label="Этап ниже">
                ↓
              </button>
              <button type="button" className="btn btn-ghost btn-sm" disabled={stages.length === 1} onClick={() => setStages(stages.filter((_, j) => j !== i))} aria-label="Удалить этап">
                ×
              </button>
            </span>
          </div>

          {stage.userIds.length === 0 && <span className="faint">Добавьте участников этапа</span>}
          {stage.userIds.map((id, k) => {
            const u = person(id);
            return (
              <div key={id} className="step-edit-row">
                {stage.mode === 'sequential' && (
                  <span className="faint" style={{ width: 22 }}>
                    {k + 1}.
                  </span>
                )}
                <div style={{ flex: 1 }}>
                  <Person name={u?.fullName ?? '…'} sub={u?.position} />
                </div>
                {stage.mode === 'sequential' && (
                  <>
                    <button type="button" className="btn btn-ghost btn-sm" disabled={k === 0} onClick={() => setStage(i, { userIds: move(stage.userIds, k, -1) })} aria-label="Раньше">
                      ↑
                    </button>
                    <button type="button" className="btn btn-ghost btn-sm" disabled={k === stage.userIds.length - 1} onClick={() => setStage(i, { userIds: move(stage.userIds, k, 1) })} aria-label="Позже">
                      ↓
                    </button>
                  </>
                )}
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setStage(i, { userIds: stage.userIds.filter((x) => x !== id) })} aria-label={`Убрать ${u?.fullName ?? ''}`}>
                  ×
                </button>
              </div>
            );
          })}
          <div className="step-edit-row">
            <UserSelect value={adding[i] ?? ''} onChange={(id) => setAdding((a) => ({ ...a, [i]: id }))} exclude={[authorId, ...stage.userIds]} placeholder="Добавить сотрудника" />
            <button
              type="button"
              className="btn"
              disabled={!adding[i]}
              onClick={() => {
                setStage(i, { userIds: [...stage.userIds, adding[i]!] });
                setAdding((a) => ({ ...a, [i]: '' }));
              }}
            >
              Добавить
            </button>
          </div>

          <div className="step-edit-row wrap">
            <span className="inline-field">
              Срок
              <input
                className="input"
                type="number"
                min={1}
                max={24 * 90}
                value={stage.deadlineHours ?? ''}
                placeholder="—"
                onChange={(e) => setStage(i, { deadlineHours: e.target.value ? Math.max(1, Number(e.target.value)) : null })}
                style={{ width: 90, minWidth: 0 }}
                aria-label="Срок, часов"
              />
              ч на решение
            </span>
            <input
              className="input"
              style={{ flex: 1, minWidth: 220 }}
              value={stage.instruction ?? ''}
              maxLength={2000}
              placeholder="Комментарий участникам этапа (необязательно)"
              onChange={(e) => setStage(i, { instruction: e.target.value || null })}
            />
          </div>
        </fieldset>
      ))}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" className="btn" disabled={stages.length >= 20} onClick={() => setStages([...stages, newCustomStage(stages.length + 1)])}>
          + Этап
        </button>
        <span className="faint">Этапы идут друг за другом; внутри этапа — все сразу или по очереди. Вы сами в маршрут не входите.</span>
      </div>
      {(error || problem) && <span className="field-error">{error ?? problem}</span>}
    </div>
  );
}
